<template>
  <section data-test="dashboard-area-section" class="grid grid-cols-12 gap-4">
    <div class="mt-2 col-span-12">
      <label class="from-label text-medium text-gray-600" for="region-select">ภาค</label>
      <v-select
        data-test="region-select"
        id="region-select"
        v-model="regionComputed"
        :options="regionOptions"
        :clearable="true"
        placeholder="กรุณาเลือกภาค"
        :appendToBody="true"
        @update:modelValue="handleRegionSelected"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
        </template>
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
        </template>
      </v-select>
    </div>

    <div class="mt-2 col-span-12">
      <label class="from-label text-medium text-gray-600" for="province-select">จังหวัด</label>
      <v-select
        data-test="province-select"
        id="province-select"
        :loading="provinceOptionsLoading"
        v-model="provinceComputed"
        :options="provinceOptions"
        label="province_name"
        :clearable="true"
        placeholder="กรุณาเลือกจังหวัด"
        @update:modelValue="handleProvinceSelected"
        :appendToBody="true"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
        </template>
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
        </template>
      </v-select>
    </div>

    <div class="mt-2 col-span-12">
      <label class="from-label text-medium text-gray-600" for="amphur-select">เขต/อำเภอ</label>
      <v-select
        data-test="amphur-select"
        id="amphur-select"
        :loading="amphurOptionsLoading"
        v-model="amphurComputed"
        :options="amphurOptions"
        label="amphur_name"
        :clearable="true"
        placeholder="กรุณาเลือกเขต/อำเภอ"
        @update:modelValue="handleAmphurSelected"
        :appendToBody="true"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
        </template>
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
        </template>
      </v-select>
    </div>

    <div class="mt-2 col-span-12">
      <label class="from-label text-medium text-gray-600" for="tumbol-select">แขวง/ตำบล</label>
      <v-select
        data-test="tumbol-select"
        id="tumbol-select"
        :loading="tumbolOptionsLoading"
        v-model="tumbolComputed"
        :options="tumbolOptions"
        label="tumbol_name"
        :clearable="true"
        placeholder="กรุณาเลือกแขวง/ตำบล"
        @update:modelValue="handleTumbolSelected"
        :appendToBody="true"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
        </template>
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
        </template>
      </v-select>
    </div>

    <div class="mt-2 col-span-12">
      <label class="from-label text-medium text-gray-600" for="village-select">หมู่บ้าน</label>
      <v-select
        data-test="village-select"
        id="village-select"
        :loading="villageOptionsLoading"
        v-model="villageComputed"
        :options="villageOptions"
        label="village_name"
        :clearable="true"
        placeholder="กรุณาเลือกหมู่บ้าน"
        :appendToBody="true"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
        </template>
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
        </template>
      </v-select>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import axios from "axios";
import Swal from "sweetalert2";
import { useStore } from "vuex";
import { provinceList } from "@/utils/province-list";

const disabledAmphur = ref(false);
const disabledTumbol = ref(false);
const disabledProvince = ref(false);

const props = defineProps({
  region: [String, Object],
  province: Object,
  amphur: Object,
  tumbol: Object,
  village: Object,
});
const emits = defineEmits([
  "update:region",
  "update:province",
  "update:amphur",
  "update:tumbol",
  "update:village",
]);

// static region list (no API source for ภาค)
const regionOptions = [
  "ภาคเหนือ",
  "ภาคกลาง",
  "ภาคตะวันออกเฉียงเหนือ",
  "ภาคตะวันออก",
  "ภาคตะวันตก",
  "ภาคใต้",
];

const regionComputed = computed({
  get() {
    return props.region;
  },
  set(evt) {
    emits("update:region", evt);
  },
});
const provinceComputed = computed({
  get() {
    return props.province;
  },
  set(evt) {
    emits("update:province", evt);
  },
});
const amphurComputed = computed({
  get() {
    return props.amphur;
  },
  set(evt) {
    emits("update:amphur", evt);
  },
});
const tumbolComputed = computed({
  get() {
    return props.tumbol;
  },
  set(evt) {
    emits("update:tumbol", evt);
  },
});
const villageComputed = computed({
  get() {
    return props.village;
  },
  set(evt) {
    emits("update:village", evt);
  },
});

const store = useStore();
const provinceOptions = ref([]);
const provinceOptionsLoading = ref(false);
const amphurOptions = ref([]);
const amphurOptionsLoading = ref(false);
const tumbolOptions = ref([]);
const tumbolOptionsLoading = ref(false);
const villageOptions = ref([]);
const villageOptionsLoading = ref(false);
const keyCokeProv = computed(() =>
  parseInt(store.getters["userAuth/getUserProvinceCode"])
);
const onlineComputed = computed(() => store.getters["network/isOnline"]);
const headers = {
  Authorization: `Bearer ${store.getters["userAuth/getToken"]}`,
  "Content-Type": `application/json; charset=utf-8`,
};
const filterChange = store.getters["dashboardOverviewFilter/filterChange"];

onMounted(async () => {
  if (!onlineComputed.value) return;
  if (!store.getters["dashboardOverviewFilter/region"] && keyCokeProv.value && !filterChange) {
    regionComputed.value = provinceList.find(
      (item) => item.code == keyCokeProv.value
    )?.th.sector;
    
  }
});

const getProvinceOptions = async () => {
  provinceOptionsLoading.value = true;
  const url = `${process.env.VUE_APP_BASE_API}refadms/?sector=${regionComputed.value}`;
  const res = await axios.get(url, { headers });
  if (res.status === 200) {
    provinceOptions.value = res.data.sort((a, b) =>
      a.province_name.localeCompare(b.province_name, "th")
    );
    if (keyCokeProv.value && !provinceComputed.value && !filterChange) {
      let prov_data = res.data.find(
        (x) => x._id.province_ID == keyCokeProv.value
      );
      if (prov_data) {
        provinceComputed.value = prov_data;
      }
    }
  } else {
    Swal.fire({ icon: "warning", text: "ระบบผิดพลาด" });
  }
  provinceOptionsLoading.value = false;
};

const getAmphurOptions = async () => {
  amphurOptionsLoading.value = true;
  const url = `${process.env.VUE_APP_BASE_API}refadms/?prov=${provinceComputed.value._id.province_ID}`;
  const res = await axios.get(url, { headers });
  if (res.status === 200) {
    amphurOptions.value = res.data.sort((a, b) =>
      a.amphur_name.localeCompare(b.amphur_name, "th")
    );
    if (store.getters["userAuth/getUserAmphurCode"]?.length == 4 && !amphurComputed.value && !filterChange) {
      let amp_data = res.data.find(
        (x) => x._id.amphur_ID == store.getters["userAuth/getUserAmphurCode"]
      );
      if (amp_data) {
        amphurComputed.value = amp_data;
      }
    }
  } else {
    Swal.fire({ icon: "warning", text: "ระบบผิดพลาด" });
  }
  amphurOptionsLoading.value = false;
};

const getTumbolOptions = async () => {
  tumbolOptionsLoading.value = true;
  const url = `${process.env.VUE_APP_BASE_API}refadms/?prov=${provinceComputed.value._id.province_ID}&amp=${amphurComputed.value._id.amphur_ID}`;
  const res = await axios.get(url, { headers });
  if (res.status === 200) {
    tumbolOptions.value = res.data.sort((a, b) =>
      a.tumbol_name.localeCompare(b.tumbol_name, "th")
    );
    const tumbolCodeList = store.getters["userAuth/getAreaCodeList"].filter(code => code.length == 6)
    // if(tumbolCodeList.length > 1){
    //   const matchedTumbols = res.data.filter(x => tumbolCodeList.includes(x._id.tumbol_ID))
    //   if(matchedTumbols.length){
    //     if(!tumbolComputed.value && !filterChange){
    //       tumbolComputed.value = matchedTumbols.find(x => x._id.tumbol_ID == tumbolCodeList[0]) ?? matchedTumbols[0]
    //     }
    //   }
    // }
    // else 
    if (store.getters["userAuth/getUserTumbolCode"]?.length == 6 && !tumbolComputed.value && !filterChange && tumbolCodeList.length == 1) {
      let tam_data = res.data.find(
        (x) => x._id.tumbol_ID == store.getters["userAuth/getUserTumbolCode"]
      );
      if (tam_data) {
        tumbolComputed.value = tam_data;
      }
    }
  } else {
    Swal.fire({ icon: "warning", text: "ระบบผิดพลาด" });
  }
  tumbolOptionsLoading.value = false;
};

const getVillageOptions = async () => {
  villageOptionsLoading.value = true;
  const url = `${process.env.VUE_APP_BASE_API}refadms/?prov=${provinceComputed.value._id.province_ID}&amp=${amphurComputed.value._id.amphur_ID}&tam=${tumbolComputed.value._id.tumbol_ID}`;
  const res = await axios.get(url, { headers });
  if (res.status === 200) {
    villageOptions.value = res.data.sort((a, b) =>
      a.village_name.localeCompare(b.village_name, undefined, { numeric: true })
    );
  } else {
    Swal.fire({ icon: "warning", text: "ระบบผิดพลาด" });
  }
  villageOptionsLoading.value = false;
};

const handleRegionSelected = () => {
  provinceComputed.value = null;
  amphurComputed.value = null;
  tumbolComputed.value = null;
  villageComputed.value = null;
};
const handleProvinceSelected = () => {
  amphurComputed.value = null;
  tumbolComputed.value = null;
  villageComputed.value = null;
};

watch(regionComputed, (val) => {
  if (val) getProvinceOptions();
  else { provinceOptions.value = []; amphurOptions.value = []; tumbolOptions.value = []; villageOptions.value = []; }
}, { immediate: true });
watch(provinceComputed, (val) => {
  if (val) getAmphurOptions();
  else { amphurOptions.value = []; tumbolOptions.value = []; villageOptions.value = []; }
}, { immediate: true });
watch(amphurComputed, (val) => {
  if (val) getTumbolOptions();
  else { tumbolOptions.value = []; villageOptions.value = []; }
}, { immediate: true });
watch(tumbolComputed, (val) => {
  if (val) getVillageOptions();
  else villageOptions.value = [];
}, { immediate: true });

const handleAmphurSelected = () => {
  tumbolComputed.value = null;
  villageComputed.value = null;
};

const handleTumbolSelected = () => {
  villageComputed.value = null;
};
</script>
